import { getQueryParamSingleValue } from 'src/lib/browser';
import { createEnumClampFunction } from 'src/lib/enum';
import * as localData from 'src/lib/localData';
import { ROUTE_PARAMS } from 'src/routes';

/**
 * Platform Application modes
 * @readonly
 * @enum {string}
 */
export const APPLICATION_MODE = {
  default: 'default',
  infinityExtension: 'infinity_extension',
} as const;
export type ApplicationMode = (typeof APPLICATION_MODE)[keyof typeof APPLICATION_MODE];

export const clampApplicationMode = createEnumClampFunction(
  APPLICATION_MODE,
  APPLICATION_MODE.default
);

let applicationMode: ApplicationMode | undefined;

/**
 * Get the current application mode.
 * Mode should not change during session.
 * @returns {ApplicationMode} - application mode
 */
export const getApplicationMode = (): ApplicationMode => {
  if (applicationMode) {
    return applicationMode;
  }

  const queryMode = getQueryParamSingleValue(ROUTE_PARAMS.applicationMode) as ApplicationMode;
  const sessionMode = localData.sessionApplicationMode.read() as ApplicationMode;
  applicationMode = clampApplicationMode(queryMode || sessionMode);
  return applicationMode;
};

export const isApplicationModeInfinity = (mode?: ApplicationMode): boolean => {
  const localMode = mode || getApplicationMode();
  return localMode === APPLICATION_MODE.infinityExtension;
};

/**
 * Get the current application host, if Platform is embedded in iframe.
 * @returns {string | null} - application host
 */
export const getApplicationHost = (): string | null => {
  const queryHost = getQueryParamSingleValue(ROUTE_PARAMS.host);
  const sessionHost = localData.sessionApplicationHost.read();
  return queryHost || sessionHost || null;
};

/**
 * Get the current extension installation ID, if Platform is embedded in iframe.
 * @returns {string | null} - installation ID
 */
export const getExtensionInstallationId = (): string | null => {
  const installationId = getQueryParamSingleValue(ROUTE_PARAMS.installationId);
  return installationId || null;
};
